import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { LoadingAnimation } from '@garnish/core';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import {
  FlexContent,
  FlexLandingPage,
  useFlexContent,
  useFlexEnrollment,
} from '@order/features-payments';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTrackEffect } from '@order/Telemetry';

import { AppFooter, SignedOutView } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { NotFoundScreen } from '../NotFoundScreen';

export const HsaFsaScreen = () => {
  const isLoggedIn = useIsLoggedIn();
  const isFlexEnabled = useFeatureFlag('CELS-5522-flex-enabled');

  if (!isFlexEnabled) {
    return <NotFoundScreen />;
  }

  if (!isLoggedIn) {
    return <SignedOutView redirectTo="/hsa-fsa" />;
  }

  return <HsaFsaReady />;
};

const HsaFsaReady = () => {
  const {
    isPreparingEnrollment,
    hasFlexPaymentMethod,
    isLoadingFlexSession,
    checkFlexEligibility,
  } = useFlexEnrollment();

  useTrackEffect('hsafsa.landing_page.viewed');

  const router = useRouter();

  const entry = useContentfulContentTypeEntry<FlexContent>({ contentType: 'hsaFsaScreen' });
  const content = useFlexContent(entry.data?.fields);

  const heroCta = hasFlexPaymentMethod ? content.eligibleCta : content.eligibilityCta;
  const coverageCta = hasFlexPaymentMethod ? content.eligibleCta : content.coverageCta;
  const switchPaymentMethodCta = hasFlexPaymentMethod ? content.switchPaymentMethodCta : undefined;

  const handleCtaPress = useCallback(() => {
    if (hasFlexPaymentMethod) {
      router.navigate('/menu');
      return;
    }

    void checkFlexEligibility();
  }, [checkFlexEligibility, hasFlexPaymentMethod, router]);

  if (isPreparingEnrollment || entry.fetching) {
    return <LoadingAnimation />;
  }

  return (
    <FlexLandingPage.Container>
      <FlexLandingPage.Container.Content>
        <FlexLandingPage.Logo />

        <FlexLandingPage.Hero
          title={content.title}
          disclaimerLabel={content.disclaimerLabel}
          disclaimerDescription={content.disclaimerDescription}
          heroImageXsUrl={content.heroImageXsUrl}
          heroImageXsA11y={content.heroImageXsA11y}
          heroImageSmUrl={content.heroImageSmUrl}
          heroImageSmA11y={content.heroImageSmA11y}
          heroCta={heroCta}
          switchPaymentMethodCta={switchPaymentMethodCta}
          isLoading={isLoadingFlexSession}
          onCtaPress={handleCtaPress}
          onSwitchPaymentMethodPress={checkFlexEligibility}
        />

        <FlexLandingPage.Guide
          guideTitle={content.guideTitle}
          guideEntries={content.guideEntries}
        />

        <FlexLandingPage.Banner banner={content.banner} />

        <FlexLandingPage.Coverage
          coverageHint={content.coverageHint}
          coverageTitle={content.coverageTitle}
          coverageDescription={content.coverageDescription}
          coverageImageUrl={content.coverageImageUrl}
          coverageImageA11y={content.coverageImageA11y}
          coverageCta={coverageCta}
          isLoading={isLoadingFlexSession}
          onCtaPress={handleCtaPress}
        />

        <FlexLandingPage.FAQ
          faqTitle={content.faqTitle}
          faqDescription={content.faqDescription}
          faqEntries={content.faqEntries}
        />
      </FlexLandingPage.Container.Content>

      <AppFooter />
    </FlexLandingPage.Container>
  );
};
